/**
 * `BarsGrouped` — §F2's vertical bars, one *pair* per category (client, 2026-09-16).
 *
 * Section I of the dashboard asks one question per contractual indicator: how far is the
 * achieved figure against the commitment. The honest way to put four indicators carrying
 * four different units on one axis is to draw each of them as a **share of its own target**,
 * which is what this chart takes: `pct` per group, one shared 0–120 % axis, and a dashed
 * reference line at 100 % that means exactly "target met" for every group on it.
 *
 * Why a sibling of `BarsV` rather than a mode of it: `BarsV` is one bar per label with the
 * value printed above it and no axis at all (§G8's "one axis per chart" — a printed number
 * per bar *is* the axis). This chart has two bars per label, a real percent axis, a
 * reference line, and two lines of caption under every group. Bending `BarsV` into that
 * would have left one component with two layouts and no shared code between them.
 *
 * Three things it will not do:
 *
 * 1. **A group with no target draws nothing.** `pct: null` and `target: false` is the
 *    `no_target` row: no achieved bar, no target bar, and the group's own `note` in place of
 *    the figures. A 0 % bar there would assert a commitment the payload denies.
 * 2. **An `awaiting` group draws the target bar only**, muted, with its note underneath —
 *    the commitment is real, the first results are not in.
 * 3. **The target bar is hatched, never solid.** Two solid bars of the same hue read as one
 *    measurement against another; the diagonal `<pattern>` says the second bar is a
 *    commitment, and it survives greyscale printing, which a 35 % tint does not.
 *
 * Hover goes through the shared `ChartTip` portal (§F2) carrying the **absolute** numbers,
 * because the axis is the only place on the chart where they are not printed.
 */
import { useId, useMemo } from 'react';

import { hideChartTip, showChartTip } from './chartTipStore';

/**
 * The geometry: the percent axis on the left, the group's code under it inside the SVG, and
 * the caption pair **outside** it.
 *
 * A viewBox scales its text with the box, so a 9 px caption drawn in the SVG is 4 px on a
 * phone (review, 2026-09-16) — which is why the absolutes and the per-group note are HTML
 * under the chart, in a grid whose columns line up with the groups, and stay 11 px at every
 * width. Only the code label, at 10 px bold, is still drawn.
 */
export const BARSG_PAD_TOP = 18;
export const BARSG_PAD_BOTTOM = 22;
export const BARSG_PAD_LEFT = 34;
export const BARSG_PAD_RIGHT = 10;
/** The axis runs past 100 % so the reference line is inside the plot, not on its frame. */
export const BARSG_MAX = 120;
export const BARSG_MAX_BAR_WIDTH = 34;
/** The percent ticks drawn as gridlines; 100 carries the reference line instead. */
export const BARSG_TICKS = [0, 25, 50, 75, 100] as const;

export interface BarsGroupedItem {
  id: string;
  /** The group's own label — an indicator code, `B.2.1`. */
  label: string;
  /** Achieved as a percentage of this group's target; `null` draws no achieved bar. */
  pct: number | null;
  /** The indicator's colour: solid for achieved, hatched at 35 % for the target. */
  color: string;
  /** Whether a commitment exists at all — `false` draws no target bar either. */
  target: boolean;
  /** `23,105 / 60,000`, printed under the label. Omitted where there is nothing to print. */
  caption?: string;
  /** The unit, on its own line under the caption, so a long pair never runs into a sibling. */
  captionSub?: string;
  /** "awaiting first results" / "No target set" — printed instead of the caption. */
  note?: string;
  /** Marks the group's label with a dagger: the target was apportioned to this scope. */
  marked?: boolean;
  /** The tooltip value for each bar — the absolutes the percent axis cannot show. */
  achievedTip?: string;
  targetTip?: string;
}

export interface BarsGroupedProps {
  groups: readonly BarsGroupedItem[];
  w?: number;
  h?: number;
  /** The tooltip label of each series ("Achieved" / "Target"). */
  achievedLabel: string;
  targetLabel: string;
  refLabel?: string;
  ariaLabel?: string;
  className?: string;
}

export function BarsGrouped({
  groups,
  w = 560,
  h = 300,
  achievedLabel,
  targetLabel,
  refLabel,
  ariaLabel,
  className,
}: BarsGroupedProps) {
  /* One id per mounted chart: two charts on a page may not share a `<pattern>` id, and a
     hard-coded one is exactly the collision that makes the second chart's bars go blank. */
  const uid = useId().replace(/[^a-zA-Z0-9-]/g, '');
  const plotW = w - BARSG_PAD_LEFT - BARSG_PAD_RIGHT;
  const plotH = h - BARSG_PAD_TOP - BARSG_PAD_BOTTOM;
  const base = h - BARSG_PAD_BOTTOM;
  const y = (value: number) =>
    base - (Math.max(0, Math.min(BARSG_MAX, value)) / BARSG_MAX) * plotH;

  const bars = useMemo(() => {
    const n = Math.max(groups.length, 1);
    const pitch = plotW / n;
    const barW = Math.max(6, Math.min(BARSG_MAX_BAR_WIDTH, (pitch - 26) / 2));
    return groups.map((group, i) => {
      const center = BARSG_PAD_LEFT + pitch * (i + 0.5);
      const pct =
        typeof group.pct === 'number' && Number.isFinite(group.pct) ? group.pct : null;
      return { group, i, center, barW, pct, achievedX: center - barW - 3, targetX: center + 3 };
    });
  }, [groups, plotW]);

  /* The caption grid's own gutters, as percentages of the viewBox, so its columns sit under
     the groups at any rendered width — the SVG scales, and a pixel padding would not. */
  const capStyle = {
    paddingLeft: `${(BARSG_PAD_LEFT / w) * 100}%`,
    paddingRight: `${(BARSG_PAD_RIGHT / w) * 100}%`,
    gridTemplateColumns: `repeat(${Math.max(groups.length, 1)}, minmax(0, 1fr))`,
  };

  return (
    <div className={`barsg-wrap${className ? ` ${className}` : ''}`}>
      <svg
        className="chart barsg"
        width="100%"
        viewBox={`0 0 ${w} ${h}`}
        role="img"
        aria-label={ariaLabel ?? `${groups.length} indicators`}
        data-groups={groups.length}
        onMouseLeave={hideChartTip}
      >
        <defs>
          {bars.map(({ group, i }) => (
            <pattern
              key={group.id}
              id={`${uid}-hatch-${i}`}
              patternUnits="userSpaceOnUse"
              width={6}
              height={6}
              patternTransform="rotate(45)"
            >
              <rect width={6} height={6} fill={group.color} fillOpacity={0.18} />
              <line x1={0} y1={0} x2={0} y2={6} stroke={group.color} strokeOpacity={0.55} strokeWidth={3} />
            </pattern>
          ))}
        </defs>

        {/* The axis: gridlines and their percent ticks, in ink tokens rather than in any
            series colour (§F2/§G8). */}
        {BARSG_TICKS.map((tick) => (
          <g key={tick}>
            <line
              x1={BARSG_PAD_LEFT}
              x2={w - BARSG_PAD_RIGHT}
              y1={y(tick)}
              y2={y(tick)}
              stroke="#E6EAE4"
              strokeWidth={1}
            />
            <text
              x={BARSG_PAD_LEFT - 6}
              y={y(tick) + 3}
              textAnchor="end"
              fontSize={8.5}
              fill="#8B958A"
              data-barsg-tick={tick}
            >
              {tick}%
            </text>
          </g>
        ))}

        {/* 100 % — the one line every group on this axis shares a meaning for. */}
        <line
          x1={BARSG_PAD_LEFT}
          x2={w - BARSG_PAD_RIGHT}
          y1={y(100)}
          y2={y(100)}
          stroke="#2B6CB0"
          strokeWidth={1.2}
          strokeDasharray="5 4"
          data-barsg-refline="100"
        />
        {refLabel ? (
          <text
            x={w - BARSG_PAD_RIGHT}
            y={y(100) - 5}
            textAnchor="end"
            fontSize={8.6}
            fontWeight={700}
            fill="#2B6CB0"
            data-barsg-reflabel=""
          >
            {refLabel}
          </text>
        ) : null}

        {bars.map(({ group, i, center, barW, pct, achievedX, targetX }) => {
          const achievedH = pct === null ? 0 : Math.max(0, base - y(pct));
          const targetH = base - y(100);
          return (
            <g key={group.id} data-barsg-group={group.id}>
              {group.target && (
                <rect
                  x={targetX}
                  y={y(100)}
                  width={barW}
                  height={targetH}
                  rx={3}
                  fill={`url(#${uid}-hatch-${i})`}
                  /* A commitment with no result against it is the quietest thing in the
                     chart, not the loudest: the bar is there, at half the ink. */
                  fillOpacity={pct === null ? 0.45 : 1}
                  stroke={group.color}
                  strokeOpacity={pct === null ? 0.25 : 0.45}
                  strokeWidth={1}
                  style={{ cursor: 'pointer' }}
                  data-barsg-bar="target"
                  onMouseEnter={(e) => showChartTip(e, targetLabel, group.targetTip ?? '')}
                  onMouseMove={(e) => showChartTip(e, targetLabel, group.targetTip ?? '')}
                  onMouseLeave={hideChartTip}
                />
              )}
              {pct !== null && (
                <rect
                  x={achievedX}
                  y={y(pct)}
                  width={barW}
                  height={achievedH}
                  rx={3}
                  fill={group.color}
                  style={{ cursor: 'pointer' }}
                  data-barsg-bar="achieved"
                  data-pct={pct}
                  onMouseEnter={(e) => showChartTip(e, achievedLabel, group.achievedTip ?? '')}
                  onMouseMove={(e) => showChartTip(e, achievedLabel, group.achievedTip ?? '')}
                  onMouseLeave={hideChartTip}
                />
              )}
              {/* The percentage over the pair — the figure the reader came for, printed so
                  nothing has to be read off the axis. */}
              {pct !== null && (
                <text
                  x={center}
                  y={Math.min(y(pct), y(100)) - 6}
                  textAnchor="middle"
                  fontSize={10.5}
                  fontWeight={700}
                  fill="#22301F"
                  data-barsg-pct={group.id}
                >
                  {Math.round(pct)}%
                </text>
              )}
              <text
                x={center}
                y={h - 8}
                textAnchor="middle"
                fontSize={10}
                fontWeight={700}
                fill="#22301F"
                data-barsg-label={group.id}
              >
                {group.label}
                {group.marked ? '†' : ''}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="barsg-caps" style={capStyle} aria-hidden="true">
        {groups.map((group) => (
          <div className="barsg-cap" key={group.id}>
            {group.note ? (
              <span className="barsg-cap-note" data-barsg-note={group.id}>
                {group.note}
              </span>
            ) : (
              <>
                <span className="barsg-cap-val" data-barsg-caption={group.id}>
                  {group.caption ?? ''}
                </span>
                {group.captionSub ? (
                  <span className="barsg-cap-sub">{group.captionSub}</span>
                ) : null}
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BarsGrouped;
